import './CourseFlashcards.css';

import { fetchFlashcardsByCourseId, createFlashcard, deleteFlashcard, updateFlashcard } from '../../../services/instructorDashboard';
import { getCourseById } from '../../../services/instructors';
import { useEffect, useState, Fragment } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';


function CourseFlashcards({ course_id }) {
    const { t } = useTranslation();
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [courseChapters, setCourseChapters] = useState([]);
    const [newFlashcard, setNewFlashcard] = useState({
        question: '',
        answer: '',
        chapter_id: 0,
        course_id: course_id
    });
    let buttonDisabled = newFlashcard.question === '' || newFlashcard.answer === '' || newFlashcard.chapter_id === 0 || loading || refreshing ? true : false;

    async function handleEditFlashcard(flashcard) {
        const { value: formValues } = await Swal.fire({
            title: "Edit Flashcard",
            html: `
                <div class="input-group">
                    <label for="swal-input1">${t('instructorDashboard.question')}:</label>
                    <input id="swal-input1" class="swal2-input" value="${flashcard.question}">
                </div>
                <div class="input-group">
                    <label for="swal-input2">${t('instructorDashboard.answer')}:</label>
                    <input id="swal-input2" class="swal2-input" value="${flashcard.answer}">
                </div>
            `,
            focusConfirm: false,
            customClass: {
                popup: 'large-swal-container',
                input: 'large-swal-input',
                title: 'custom-swal-title'
            },
            width: '800px',
            showCancelButton: true,
            confirmButtonText: 'Confirm',
            confirmButtonColor: '#0B4475',
            cancelButtonColor: '#d33',
            preConfirm: () => {
                return [
                    document.getElementById("swal-input1").value,
                    document.getElementById("swal-input2").value
                ];
            }
        });
        
        if (formValues) {
            setRefreshing(true);
            const resp = await updateFlashcard(flashcard.id, {
                question: formValues[0],
                answer: formValues[1]
            });
            if (resp.success) {
                fetchFlashcards();
                Swal.fire({
                    title: "Success!",
                    text: "Flashcard has been updated.",
                    icon: "success",
                    confirmButtonColor: "#0B4475"
                })
            }
        }
    }

    async function handleDeleteFlashcard(flashcard) {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#0B4475",
            cancelButtonColor: "#d33",
            confirmButtonText: "Confirm"
          }).then(async(result) => {
            if (result.isConfirmed) {
                setRefreshing(true);
                const resp = await deleteFlashcard(flashcard.id);
                if (resp.success) {
                    fetchFlashcards();
                    Swal.fire({
                        title: "Deleted!",
                        text: "Flashcard has been deleted.",
                        icon: "success"
                      });
                }
            }
          });
    }

    async function fetchFlashcards() {
        const resp = await fetchFlashcardsByCourseId(course_id);

        if (resp.success) {
            setFlashcards(resp.data);
            if (courseChapters.length === 0) {
                fetchCourseChapters();
            }
        }
        if (flashcards.length > 0) {
            setRefreshing(false);
        }
    }

    async function fetchCourseChapters() {
        const resp = await getCourseById(course_id);
        if (resp.success) {
            let setter = [];
            for (let i = 0; i < resp.data.chapter.length; i++) {
                let chapter = resp.data.chapter[i];
                setter.push(chapter);
            }
            setCourseChapters(setter);
            setNewFlashcard({
                ...newFlashcard,
                chapter_id: setter[0].id
            });
        }
        setLoading(false);
    }

    async function handleCreateFlashcard() {
        setRefreshing(true);
        const resp = await createFlashcard(newFlashcard);
        if (resp.success) {
            fetchFlashcards();
            setNewFlashcard({
                question: '',
                answer: '',
                chapter_id: courseChapters[0].id,
                course_id: course_id
            });
            Swal.fire({
                title: "Success!",
                text: "Flashcard has been created.",
                icon: "success",
                confirmButtonColor: "#0B4475"
            });
        } else {
            setRefreshing(false);
            Swal.fire({
                title: "Error!",
                text: "Failed to create flashcard.",
                icon: "error",
                confirmButtonColor: "#0B4475"
            });
        }
    }

    async function openAllFlashcards() {
        if (flashcards.length === 0) {
            await Swal.fire({
                title: t('instructorDashboard.flashcards'),
                text: t('instructorDashboard.noFlashcardsFound'),
                confirmButtonColor: '#0B4475',
                customClass: {
                    title: 'custom-swal-title'
                }
            });
            return;
        }

        // Add these functions to handle the button clicks
        window.handleEditFlashcard = handleEditFlashcard;
        window.handleDeleteFlashcard = handleDeleteFlashcard;

        const flashcardsHtml = flashcards.map(flashcard => `
            <div class="flashcard-details">
                <div class="flashcard-item">
                    <strong>Chapter:</strong> 
                    <span>${flashcard.chapter.name}</span>
                </div>
                <div class="flashcard-item">
                    <strong>${t('instructorDashboard.question')}:</strong> 
                    <span>${flashcard.question}</span>
                </div>
                <div class="flashcard-item">
                    <strong>${t('instructorDashboard.answer')}:</strong> 
                    <span>${flashcard.answer}</span>
                </div>
                <div class="flashcard-actions" style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 10px;">
                    <button 
                        onclick='window.handleEditFlashcard(${JSON.stringify(flashcard).replace(/'/g, "&apos;")})' 
                        style="background: none; border: none; cursor: pointer; color: #0B4475;"
                    >
                        <svg style="width: 24px; height: 24px;" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                    </button>
                    <button 
                        onclick='window.handleDeleteFlashcard(${JSON.stringify(flashcard).replace(/'/g, "&apos;")})' 
                        style="background: none; border: none; cursor: pointer; color: #d33;"
                    >
                        <svg style="width: 24px; height: 24px;" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                    </button>
                </div>
                <hr class="flashcard-divider">
            </div>
        `).join('');

        await Swal.fire({
            title: t('instructorDashboard.allFlashcards'),
            html: `<div class="flashcard-list-container">${flashcardsHtml}</div>`,
            customClass: {
                popup: 'large-swal-container',
                title: 'custom-swal-title'
            },
            width: '800px',
            confirmButtonText: t('instructorDashboard.close'),
            confirmButtonColor: '#0B4475'
        });

        // Clean up the window functions
        delete window.handleEditFlashcard;
        delete window.handleDeleteFlashcard;
    }

    useEffect(() => {
        fetchFlashcards();
    }, []);

    return (
        <div className="course-flashcards">
            <h1>{t('instructorDashboard.createFlashcard')}</h1>

            <div className="flashcard-create">
                <div className="flashcard-create-question">
                    <span>{t('instructorDashboard.q')}: </span>
                    <input 
                        type="text" 
                        placeholder={t('instructorDashboard.question')} 
                        value={newFlashcard.question}
                        onChange={(e) => setNewFlashcard({
                            ...newFlashcard,
                            question: e.target.value
                        })}
                    />
                </div>
                <div className="flashcard-create-answer">
                    <span>{t('instructorDashboard.a')}: </span>
                    <textarea
                        placeholder={t('instructorDashboard.answer')} 
                        value={newFlashcard.answer}
                        onChange={(e) => setNewFlashcard({
                            ...newFlashcard,
                            answer: e.target.value
                        })}
                    />
                </div>
                <div className="flashcard-create-footer">
                    <select
                        value={newFlashcard.chapter_id}
                        onChange={(e) => setNewFlashcard({
                            ...newFlashcard,
                            chapter_id: parseInt(e.target.value)
                        })}
                    >
                        {loading ? (
                            <option>{t('instructorDashboard.loadingChapters')}</option>
                        ) : (
                            courseChapters.map((chapter, index) => (
                                <option value={chapter.id} key={index}>
                                    {chapter.name}
                                </option>
                            ))
                        )}
                    </select>
                    <button disabled={buttonDisabled} style={buttonDisabled ? { backgroundColor: 'grey' } : {}} onClick={handleCreateFlashcard}>{t('instructorDashboard.create')}</button>
                </div>
            </div>

            <h2>{t('instructorDashboard.allFlashcards')}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button 
                    onClick={openAllFlashcards} 
                    disabled={loading || refreshing} 
                    style={{ backgroundColor: loading || refreshing ? 'grey' : '#0B4475' }} 
                    className="fetch-flashcards-button"
                >
                    {t('instructorDashboard.allFlashcards')}
                </button>
                {(loading || refreshing) && (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        height: '100%',
                        marginTop: '30px'
                    }}>
                        <CircularProgress />
                    </Box>
                )}
            </div>
            
            {!loading && !refreshing && flashcards.length === 0 && (
                <div className="flashcard-list-empty">
                    <h3>{t('instructorDashboard.noFlashcardsFound')}</h3>
                </div>
            )}
        </div>
    );
}

export default CourseFlashcards;